/**
 * Design tokens for JNote Web.
 *
 * Ported item-by-item from `Sources/JNote/Design/Tokens.swift` — that file is
 * the single source of truth. Every value here has a documented counterpart in
 * the macOS app; do not introduce colours, radii or type sizes that are not
 * traceable back to it.
 */

function hex(value: number): string {
  return `#${value.toString(16).padStart(6, '0')}`;
}

export const palette = {
  // Surfaces
  bg: hex(0x0a0a0a),
  bgElevated: hex(0x121212),
  /** How much the content column covers the frosted material underneath. */
  contentTintOpacity: 0.82,

  // Sidebar (panda-green chrome sampled from the app icon)
  sidebarTop: hex(0x00260a),
  sidebarMid: hex(0x0a3c12),
  sidebarBottom: hex(0x105a12),
  sidebarFg: hex(0xeaf3e6),
  sidebarFgMuted: hex(0x8fbf88),
  sidebarFgFaint: hex(0x5a8c54),
  sidebarAccent: hex(0x5cbf0c),
  sidebarBorderBase: hex(0x0c3a10),
  sidebarBorderOpacity: 0.72,
  sidebarHoverOpacity: 0.14,
  sidebarActiveOpacity: 0.22,
  sidebarDragPreview: hex(0x0a2a0c),

  // Content-column neutrals
  fg: hex(0xe8e8e8),
  fgMuted: hex(0x9a9a9a),
  fgFaint: hex(0x6d6d6d),
  accent: hex(0xffffff),

  // State
  success: hex(0x3fb950),
  warning: hex(0xf2b84b),
  danger: hex(0xf85649),

  // Translucent surfaces (alpha over whatever is behind)
  codeBgAlpha: 0.05,
  cardBgAlpha: 0.035,
  cardHeaderAlpha: 0.055,
  cardBorderAlpha: 0.1,
  inputBgAlpha: 0.3,
  borderAlpha: 0.08,
  hoverAlpha: 0.06,
  activeAlpha: 0.1,
} as const;

export const metrics = {
  sidebarWidth: 266,
  sidebarWidthMin: 180,
  sidebarWidthMax: 440,
  contentMinWidth: 520,
  titlebarHeight: 40,
  /** Shared min-height of the note-list and editor top bars so both panes'
   *  bottom borders sit on the same line. */
  paneTopbarHeight: 55,
  rowRadius: 6,
  panelRadius: 8,
  iconSize: 16,
  iconStroke: 1.75,
} as const;

export const typography = {
  uiSize: 13,
  labelSize: 11,
  labelWeight: 500,
  titleSize: 22,
  titleWeight: 600,
  editorSize: 13.5,
  uiFamily:
    "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
  monoFamily:
    "ui-monospace, SFMono-Regular, Menlo, 'SF Mono', 'JetBrains Mono', monospace",
} as const;

/**
 * Overlay scrollbar metrics — Web-only.
 *
 * The macOS app rides the system overlay `NSScroller`, which exposes no
 * customisable tokens, so this group approximates the system knob instead:
 * a ~7pt capsule inset from the pane edge that appears on scroll and fades
 * out after a short idle. Consumed by `lib/ui/overlayScroll.ts` and the
 * `--ob-*` custom properties below.
 */
export const scrollbar = {
  /** Capsule thickness, in px. */
  thumbSize: 7,
  /** Gap between the capsule and the pane edge, in px. */
  thumbInset: 3,
  /** Shortest capsule allowed, so huge lists stay grabbable. */
  thumbMinLength: 28,
  /** Idle time after scrolling stops before the capsule fades out, in ms. */
  idleDelayMs: 700,
  /** Fade duration, in ms. */
  fadeMs: 350,
} as const;

/**
 * Three-pane column metrics — Web-only.
 *
 * The sidebar range mirrors the macOS split (`Tokens.sidebarWidthMin/Max`,
 * persisted like `persistSidebarWidth` in UIState.swift). The note-list
 * column and the editor's floor are web additions — the Mac editor splits
 * editor|preview instead, so this column has no macOS counterpart.
 */
export const columns = {
  /** Default note-list column width (the previous fixed width). */
  noteListWidth: 280,
  noteListWidthMin: 220,
  noteListWidthMax: 480,
  /** The editor keeps at least this much while the note list is resized. */
  editorMinWidth: 360,
} as const;

/**
 * Responsive breakpoints — Web-only.
 *
 * The macOS app is window-sized so it has no counterpart; the web client
 * switches the library to the single-column view stack (list ⇄ editor with
 * a drawer sidebar) at or below `mobileMax`. The media queries in app.css
 * use the same literal — keep them in sync.
 */
export const breakpoints = {
  /** Viewports at or below this width get the mobile layout. */
  mobileMax: 768,
} as const;

/**
 * Sidebar background gradient, matching `SidebarView.swift`.
 *
 * The macOS gradient runs `startPoint: .bottom -> endPoint: .top`, so the
 * token named `top` sits at the *visual* bottom and vice versa (the Swift
 * comment calls this the "reversed" gradient). The stop order below is
 * therefore token order, not visual order:
 *   visual top    -> Sidebar.bottom (fluorescent lime)
 *   visual middle -> Sidebar.mid    (48%)
 *   visual bottom -> Sidebar.top    (forest ink)
 */
export const sidebarGradient = `linear-gradient(to top, ${palette.sidebarTop} 0%, ${palette.sidebarMid} 48%, ${palette.sidebarBottom} 100%)`;

/**
 * CSS custom properties mirroring the tokens above. Injected once at startup
 * so both hand-written CSS and inline styles can reference a single source.
 */
const CSS_VARIABLES = `
:root {
  --bg: ${palette.bg};
  --bg-elevated: ${palette.bgElevated};
  --content-tint-opacity: ${palette.contentTintOpacity};

  --sidebar-top: ${palette.sidebarTop};
  --sidebar-mid: ${palette.sidebarMid};
  --sidebar-bottom: ${palette.sidebarBottom};
  --sidebar-fg: ${palette.sidebarFg};
  --sidebar-fg-muted: ${palette.sidebarFgMuted};
  --sidebar-fg-faint: ${palette.sidebarFgFaint};
  --sidebar-accent: ${palette.sidebarAccent};
  --sidebar-border: rgba(12, 58, 16, ${palette.sidebarBorderOpacity});
  --sidebar-hover: rgba(92, 191, 12, ${palette.sidebarHoverOpacity});
  --sidebar-active: rgba(92, 191, 12, ${palette.sidebarActiveOpacity});
  --sidebar-drag-preview: ${palette.sidebarDragPreview};

  --fg: ${palette.fg};
  --fg-muted: ${palette.fgMuted};
  --fg-faint: ${palette.fgFaint};
  --accent: ${palette.accent};

  --success: ${palette.success};
  --warning: ${palette.warning};
  --danger: ${palette.danger};

  --code-bg: rgba(255, 255, 255, ${palette.codeBgAlpha});
  --card-bg: rgba(255, 255, 255, ${palette.cardBgAlpha});
  --card-header: rgba(255, 255, 255, ${palette.cardHeaderAlpha});
  --card-border: rgba(255, 255, 255, ${palette.cardBorderAlpha});
  --input-bg: rgba(0, 0, 0, ${palette.inputBgAlpha});
  --border: rgba(255, 255, 255, ${palette.borderAlpha});
  --hover: rgba(255, 255, 255, ${palette.hoverAlpha});
  --active: rgba(255, 255, 255, ${palette.activeAlpha});

  --sidebar-width: ${metrics.sidebarWidth}px;
  --sidebar-width-min: ${metrics.sidebarWidthMin}px;
  --sidebar-width-max: ${metrics.sidebarWidthMax}px;
  --note-list-width: ${columns.noteListWidth}px;
  --note-list-width-min: ${columns.noteListWidthMin}px;
  --note-list-width-max: ${columns.noteListWidthMax}px;
  --content-min-width: ${metrics.contentMinWidth}px;
  --titlebar-height: ${metrics.titlebarHeight}px;
  --pane-topbar-height: ${metrics.paneTopbarHeight}px;
  --row-radius: ${metrics.rowRadius}px;
  --panel-radius: ${metrics.panelRadius}px;
  --icon-size: ${metrics.iconSize}px;

  --font-ui: ${typography.uiFamily};
  --font-mono: ${typography.monoFamily};
  --font-ui-size: ${typography.uiSize}px;
  --font-label-size: ${typography.labelSize}px;
  --font-title-size: ${typography.titleSize}px;
  --font-editor-size: ${typography.editorSize}px;

  --ob-thumb-size: ${scrollbar.thumbSize}px;
  --ob-thumb-inset: ${scrollbar.thumbInset}px;
  --ob-thumb-min: ${scrollbar.thumbMinLength}px;
  --ob-fade: ${scrollbar.fadeMs}ms;
  /* Capsule fills read as the system overlay knob on the Obsidian base. */
  --ob-thumb: rgba(255, 255, 255, 0.32);
  --ob-thumb-hover: rgba(255, 255, 255, 0.5);
  --ob-thumb-drag: rgba(255, 255, 255, 0.62);
}
`;

let injected = false;

/** Injects the token custom properties into the document (idempotent). */
export function injectTokenVariables(): void {
  if (injected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.setAttribute('data-jnote-tokens', '');
  style.textContent = CSS_VARIABLES;
  document.head.append(style);
  injected = true;
}
